import { useMemo } from 'react'

import { ALL_RESULTS, BidType } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useBidHistoriesPageQuery } from '@/features/bidding/hooks/useBidHistoriesPageQuery'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidHistory } from '@/features/bidding/types/bidHistory'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import * as hasCapacityBidUtils from '@/features/bidding/utils/hasCapacityBid'

export type UseBidOfferCapacityBidsResult = {
  hasCapacityBidOnThisDay: (activationGroupUuid: string) => BidHistory | undefined
  hasCapacityBidWithAcceptedStatusOnThisDay: (activationGroupUuid: string) => boolean
  getAcceptedCapacityBidOnThisDay: (activationGroupUuid: string) => ExtendedBid | undefined
  getLatestCapacityBidOnThisDay: (activationGroupUuid: string) => ExtendedBid | undefined
  isLoading: boolean
}

/**
 * Hook to fetch capacity bid data for a specific delivery day, market program, and portfolio.
 * Provides utility functions to check for capacity bids and accepted capacity bids.
 *
 * @param groupParams - The grouped bid parameters containing deliveryDay, marketProgram, portfolioCode, and bidType.
 * @param options - Optional configuration
 * @param options.enabled - Whether to fetch capacity bids. When false, returns safe defaults immediately.
 * @returns An object containing utility functions and loading state
 *
 * @example
 * ```tsx
 * const { hasCapacityBidOnThisDay, isLoading } = useBidOfferCapacityBids(groupParams, { enabled: true })
 *
 * if (isLoading) return <Spinner />
 *
 * const bidHistory = hasCapacityBidOnThisDay('activation-group-uuid')
 * ```
 */
export function useBidOfferCapacityBids(
  groupParams: GroupedBidParams,
  options?: { enabled?: boolean },
): UseBidOfferCapacityBidsResult {
  const { selectedCountry } = useBiddingContext()
  const enabled = options?.enabled ?? true

  const { bidHistoriesPage, isLoading } = useBidHistoriesPageQuery(
    {
      pagination: { pageSize: ALL_RESULTS, page: 0 },
      sorting: [],
      filter: {
        items: [
          { field: 'portfolio', operator: 'equals', value: groupParams.portfolioCode },
          { field: 'marketProgram', operator: 'equals', value: groupParams.marketProgram },
          { field: 'deliveryDay', operator: 'equals', value: groupParams.deliveryDay },
          { field: 'bidType', operator: 'equals', value: BidType.CAPACITY },
        ],
      },
      countryFilter: selectedCountry,
    },
    {
      enabled: enabled,
    },
  )

  const bidHistories = bidHistoriesPage?.bidHistories ?? []

  const hasCapacityBidOnThisDay = useMemo(
    () => (activationGroupUuid: string) =>
      hasCapacityBidUtils.hasCapacityBidOnThisDay(bidHistories, activationGroupUuid),
    [bidHistories],
  )

  const hasCapacityBidWithAcceptedStatusOnThisDay = useMemo(
    () => (activationGroupUuid: string) =>
      hasCapacityBidUtils.hasCapacityBidWithAcceptedStatusOnThisDay(bidHistories, activationGroupUuid),
    [bidHistories],
  )

  const getAcceptedCapacityBidOnThisDay = useMemo(
    () => (activationGroupUuid: string) =>
      hasCapacityBidUtils.getAcceptedCapacityBidOnThisDay(bidHistories, activationGroupUuid),
    [bidHistories],
  )

  const getLatestCapacityBidOnThisDay = useMemo(
    () => (activationGroupUuid: string) =>
      hasCapacityBidUtils.getLatestCapacityBidOnThisDay(bidHistories, activationGroupUuid),
    [bidHistories],
  )

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  // The object should only change when the functions or loading state changes
  return useMemo(
    () => ({
      hasCapacityBidOnThisDay,
      hasCapacityBidWithAcceptedStatusOnThisDay,
      getAcceptedCapacityBidOnThisDay,
      getLatestCapacityBidOnThisDay,
      isLoading,
    }),
    [
      hasCapacityBidOnThisDay,
      hasCapacityBidWithAcceptedStatusOnThisDay,
      getAcceptedCapacityBidOnThisDay,
      getLatestCapacityBidOnThisDay,
      isLoading,
    ],
  )
}
